import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import HeroSection from './components/HeroSection';
import WhatIsSection from './components/WhatIsSection';
import ComparisonSection from './components/ComparisonSection';
import CategoriesSection from './components/CategoriesSection';
import SafetySection from './components/SafetySection';
import CTASection from './components/CTASection';

const SITE_URL = import.meta.env.VITE_SITE_URL;

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": `${SITE_URL}/`,
      "name": "Kakobuy Spreadsheet Polska",
      "description": "Kakobuy Spreadsheet Polska — największa baza produktów, linków, QC i poradników zakupowych. Znajdź najlepsze okazje szybciej.",
      "inLanguage": "pl-PL",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "Kakobuy Spreadsheet Polska",
      "url": `${SITE_URL}/`,
      "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
      "description": "Największy polski hub wiedzy dla użytkowników Kakobuy Spreadsheet. Baza produktów, poradniki, analizy QC i bezpieczeństwo zakupów.",
      "sameAs": [
        "https://kaspreadsheet.com"
      ]
    }
  ]
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd} />
      <Navbar />

      <main>
        <HeroSection />
        <WhatIsSection />
        <ComparisonSection />
        <CategoriesSection />
        <SafetySection />

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                Poradniki i artykuły SEO
              </h2>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto">
                Sprawdź nasze poradniki, które pomogą Ci bezpiecznie i efektywnie korzystać z Kakobuy Spreadsheet.
                Oryginalne artykuły napisane specjalnie dla polskich użytkowników.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Link
                to="/artykul/kakobuy-spreadsheet-polska-poradnik"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20open%20laptop%20showing%20spreadsheet%20and%20shopping%20interface%2C%20warm%20beige%20cream%20tones%2C%20Scandinavian%20desk%20aesthetic%2C%20soft%20natural%20daylight%2C%20editorial%20photography%2C%20clean%20composition%2C%20organic%20textures%2C%20high%20detail%2C%20elegant%20business%20vibe&width=600&height=338&seq=kakobuy-article-card-1&orientation=landscape"
                    alt="Kakobuy Spreadsheet Polska - kompletny poradnik 2026"
                    title="Kakobuy Spreadsheet Polska Poradnik"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold mb-3">
                    Poradnik
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    Kakobuy Spreadsheet Polska – Kompletny Poradnik 2026
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Dowiedz się wszystkiego o Kakobuy Spreadsheet: jak działa, jak znaleźć najlepsze produkty, czy jest bezpieczny i jak zacząć oszczędzać na zakupach.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">10 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/czy-kakobuy-jest-bezpieczny"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Security%20shield%20and%20padlock%20concept%20with%20elegant%20shopping%20bags%20on%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20trust%20and%20safety%20atmosphere%2C%20editorial%20product%20photography%2C%20clean%20minimal%20composition%2C%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20reassuring%20and%20professional%20mood&width=600&height=338&seq=kakobuy-article-card-2&orientation=landscape"
                    alt="Czy Kakobuy jest bezpieczny - analiza bezpieczeństwa"
                    title="Kakobuy bezpieczeństwo - poradnik"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-semibold mb-3">
                    Bezpieczeństwo
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-accent-700 transition-colors">
                    Czy Kakobuy jest bezpieczny? Pełna analiza ryzyka
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Pełna analiza bezpieczeństwa zakupów przez Kakobuy. Sprawdź, na co uważać i jak chronić swoje dane podczas zakupów online.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">8 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/kakobuy-dla-poczatkujacych"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-secondary-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cozy%20beginner%20workspace%20with%20open%20laptop%20showing%20step%20by%20step%20tutorial%2C%20notebook%20with%20handwritten%20notes%20and%20coffee%20cup%2C%20warm%20morning%20light%2C%20beige%20cream%20tones%2C%20Scandinavian%20learning%20atmosphere%2C%20editorial%20photography%2C%20clean%20composition%2C%20organic%20textures%2C%20welcoming%20friendly%20mood%2C%20high%20detail&width=600&height=338&seq=kakobuy-article-card-3&orientation=landscape"
                    alt="Kakobuy dla początkujących - poradnik krok po kroku"
                    title="Kakobuy poradnik dla początkujących 2026"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-semibold mb-3">
                    Dla początkujących
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-secondary-700 transition-colors">
                    Kakobuy dla początkujących – Jak zacząć bez błędów
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Zaczynasz przygodę z Kakobuy? Ten poradnik krok po kroku przeprowadzi Cię przez cały proces — od rejestracji po pierwszą paczkę.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">12 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/najlepsze-kakobuy-finds"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Exciting%20fashion%20treasure%20discovery%20concept%2C%20sneakers%20clothing%20accessories%20artfully%20arranged%20like%20found%20gems%2C%20warm%20cream%20background%2C%20golden%20hour%20natural%20light%2C%20editorial%20product%20photography%2C%20elegant%20composition%2C%20discovery%20and%20luxury%20vibe%2C%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures&width=600&height=338&seq=kakobuy-article-card-4&orientation=landscape"
                    alt="Najlepsze Kakobuy Finds - top produkty i okazje 2026"
                    title="Kakobuy Spreadsheet - najlepsze znaleziska"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-semibold mb-3">
                    Znaleziska
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-accent-700 transition-colors">
                    Najlepsze Kakobuy Finds 2026 – Top produkty i okazje
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Odkryj najlepsze znaleziska w Kakobuy Spreadsheet. Sneakersy, ubrania, akcesoria — sprawdzone linki i jakość QC.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">14 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/kakobuy-spreadsheet-polska-poradnik"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Organized%20fashion%20product%20catalog%20with%20shoes%20hoodies%20and%20jackets%20neatly%20arranged%20on%20warm%20beige%20surface%2C%20soft%20diffused%20daylight%2C%20clean%20editorial%20product%20photography%2C%20minimal%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20professional%20ecommerce%20catalog%20mood&width=600&height=338&seq=kakobuy-article-card-5&orientation=landscape"
                    alt="Jak działa Kakobuy Spreadsheet - instrukcja obsługi"
                    title="Kakobuy Spreadsheet instrukcja"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold mb-3">
                    Poradnik
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    Jak działa Kakobuy Spreadsheet? Pełna instrukcja
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Szczegółowa instrukcja obsługi Kakobuy Spreadsheet. Od otwierania arkusza po finalizację zamówienia — wszystko wyjaśnione.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">7 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/czy-kakobuy-jest-bezpieczny"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Magnifying%20glass%20examining%20fashion%20product%20details%20on%20warm%20cream%20background%2C%20quality%20control%20inspection%20concept%2C%20soft%20natural%20daylight%2C%20editorial%20product%20photography%2C%20clean%20minimal%20composition%2C%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20professional%20quality%20assurance%20mood&width=600&height=338&seq=kakobuy-article-card-6&orientation=landscape"
                    alt="Kakobuy QC - system kontroli jakości produktów"
                    title="Kakobuy Spreadsheet QC guide"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-semibold mb-3">
                    Jakość
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-accent-700 transition-colors">
                    Kakobuy QC – Jak sprawdzać jakość produktów?
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    System kontroli jakości QC w Kakobuy. Naucz się analizować zdjęcia i podejmować właściwe decyzje zakupowe.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">6 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/kakobuy-dla-poczatkujacych"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-secondary-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Cardboard%20shipping%20box%20with%20packing%20tape%20and%20fashion%20items%20beside%20it%20on%20warm%20cream%20surface%2C%20international%20delivery%20concept%2C%20soft%20natural%20daylight%2C%20editorial%20product%20photography%2C%20clean%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20reliable%20shipping%20mood&width=600&height=338&seq=kakobuy-article-card-7&orientation=landscape"
                    alt="Kakobuy wysyłka do Polski - metody i koszty"
                    title="Kakobuy Spreadsheet wysyłka Polska"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-semibold mb-3">
                    Wysyłka
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-secondary-700 transition-colors">
                    Kakobuy wysyłka do Polski – Metody, koszty i porady
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Wszystko o wysyłce z Kakobuy do Polski: dostępne metody, orientacyjne koszty, czas dostawy i jak uniknąć problemów z cłem.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">9 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/najlepsze-kakobuy-finds"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylish%20sneakers%20close%20up%20with%20shopping%20tags%20and%20price%20comparison%20concept%2C%20warm%20cream%20background%2C%20soft%20natural%20daylight%2C%20editorial%20product%20photography%2C%20clean%20minimal%20composition%2C%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20savvy%20shopping%20and%20bargain%20hunting%20mood&width=600&height=338&seq=kakobuy-article-card-8&orientation=landscape"
                    alt="Kakobuy opinie i recenzje użytkowników 2026"
                    title="Kakobuy Spreadsheet opinie"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-semibold mb-3">
                    Recenzje
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-accent-700 transition-colors">
                    Kakobuy opinie – Co mówią polscy użytkownicy?
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Zestawienie opinii i recenzji polskich użytkowników Kakobuy. Sprawdź, czy warto — szczere doświadczenia kupujących.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">11 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>

              <Link
                to="/artykul/kakobuy-dla-poczatkujacych"
                className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-secondary-300 transition-all cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Calculator%20and%20receipt%20beside%20fashion%20items%20on%20warm%20cream%20desk%2C%20budget%20planning%20and%20cost%20saving%20concept%2C%20soft%20natural%20daylight%2C%20editorial%20product%20photography%2C%20clean%20Scandinavian%20aesthetic%2C%20high%20detail%2C%20organic%20textures%2C%20smart%20financial%20planning%20and%20shopping%20savings%20mood&width=600&height=338&seq=kakobuy-article-card-9&orientation=landscape"
                    alt="Kakobuy koszty i cła - ile naprawdę kosztują zakupy"
                    title="Kakobuy Spreadsheet koszty cła VAT"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-semibold mb-3">
                    Koszty
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-secondary-700 transition-colors">
                    Kakobuy koszty i cła – Ile naprawdę zapłacisz?
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    Przejrzysty przewodnik po kosztach: prowizje, wysyłka, cło i VAT. Policz całkowity koszt zakupów, zanim klikniesz "kup".
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-foreground-400">7 min czytania</span>
                    <span className="text-xs text-foreground-300">•</span>
                    <span className="text-xs text-foreground-400">2026</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
    </div>
  );
}